/**
 * FiasAIVerbosePanel — admin-only debug timeline for the assistant
 * (the arche-builder verbose contract): system prompt, tool list, and
 * full tool inputs/results as they stream, each entry expandable.
 *
 * Rendered only when the admin verbose toggle is on; the server also
 * gates emission on isAdmin, so this is presentation, not the boundary.
 */

import { useState } from 'react';
import { ChevronRight, Terminal } from 'lucide-react';

export interface FiasAIVerboseEvent {
  type: string;
  timestamp: string;
  data: unknown;
}

const TYPE_STYLES: Record<string, string> = {
  system_prompt: 'text-purple-500',
  tools_available: 'text-blue-500',
  tool_call: 'text-amber-500',
  tool_result: 'text-green-600',
};

function eventBody(event: FiasAIVerboseEvent): string {
  const data = event.data as Record<string, unknown>;
  if (event.type === 'system_prompt' && typeof data?.prompt === 'string') {
    return data.prompt;
  }
  try {
    return JSON.stringify(event.data, null, 2);
  } catch {
    return String(event.data);
  }
}

function eventLabel(event: FiasAIVerboseEvent): string {
  const data = event.data as Record<string, unknown>;
  const tool = typeof data?.toolName === 'string' ? ` — ${data.toolName}` : '';
  const err = data?.isError === true ? ' (error)' : '';
  return `${event.type}${tool}${err}`;
}

function VerboseEntry({ event }: { event: FiasAIVerboseEvent }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border/50 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-2 py-1 text-left hover:bg-muted/50"
      >
        <ChevronRight
          className={`h-3 w-3 flex-shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
        />
        <span className={`font-mono text-[11px] ${TYPE_STYLES[event.type] ?? ''}`}>
          {eventLabel(event)}
        </span>
        <span className="ml-auto font-mono text-[10px] text-muted-foreground">
          {event.timestamp.slice(11, 23)}
        </span>
      </button>
      {open && (
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words bg-muted/40 px-3 py-2 font-mono text-[11px]">
          {eventBody(event)}
        </pre>
      )}
    </div>
  );
}

export function FiasAIVerbosePanel({ events }: { events: FiasAIVerboseEvent[] }) {
  if (events.length === 0) return null;
  return (
    <div
      className="mt-2 rounded-lg border border-dashed border-border bg-background/60"
      data-testid="fias-ai-verbose-panel"
    >
      <div className="flex items-center gap-1.5 border-b border-border/50 px-2 py-1 text-[11px] font-medium text-muted-foreground">
        <Terminal className="h-3 w-3" /> Verbose ({events.length})
      </div>
      <div className="max-h-80 overflow-y-auto">
        {events.map((e, i) => (
          <VerboseEntry key={`${e.timestamp}-${i}`} event={e} />
        ))}
      </div>
    </div>
  );
}
